import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';

const OUTCOME = {
  successful:   { label: '✅ Worked',       color: 'var(--green)',  tip: 'Mark as successful — continue this practice' },
  unsuccessful: { label: '❌ Didn\'t work', color: 'var(--red)',    tip: 'Mark as unsuccessful — stop trying this' },
  not_done:     { label: '🔄 Redo',         color: 'var(--orange)', tip: 'Carry forward to this sprint' },
};

export default function CarryoverPanel({ sessionId, teamId, isFacilitator, onCarryForward }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!teamId) return;
    setLoading(true);
    api.actions.carryover(teamId, sessionId)
      .then(data => {
        setItems(data || []);
        if ((data || []).length > 0) setOpen(true);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [teamId, sessionId]);

  if (loading || items.length === 0) return null;

  const handleOutcome = async (item, outcome) => {
    setPendingId(item.id);
    setError('');
    const prev = items;
    setItems(items.filter(i => i.id !== item.id));
    try {
      if (outcome === 'not_done') {
        const result = await api.actions.carryForward(item.id, sessionId);
        if (onCarryForward && result?.new_action) onCarryForward(result.new_action);
      } else {
        await api.actions.setOutcome(item.id, outcome);
      }
    } catch (e) {
      setError(e.message || 'Failed to update');
      setItems(prev);
    } finally {
      setPendingId(null);
    }
  };

  const groupedByRetro = items.reduce((acc, item) => {
    const key = item.retro_name || 'Previous retro';
    if (!acc[key]) acc[key] = { date: item.retro_date, items: [] };
    acc[key].items.push(item);
    return acc;
  }, {});

  return (
    <div className="card mb-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
      <button
        className="btn btn-ghost btn-sm w-full"
        style={{ justifyContent: 'space-between', padding: 0 }}
        onClick={() => setOpen(o => !o)}
      >
        <span className="text-sm font-bold">
          📋 From Previous Retros
          <span className="badge badge-purple" style={{ marginLeft: 6, fontSize: 10 }}>{items.length}</span>
        </span>
        <span className="text-xs text-muted">{open ? '▲' : '▼'}</span>
      </button>

      {error && (
        <div className="toast toast-error mt-2 text-xs" style={{ padding: '6px 10px' }}>
          {error}
          <button className="btn btn-ghost btn-sm" style={{ padding: 0, marginLeft: 8 }} onClick={() => setError('')}>✕</button>
        </div>
      )}

      {open && (
        <div className="mt-3 flex-col gap-4">
          {Object.entries(groupedByRetro).map(([retroName, group]) => (
            <div key={retroName}>
              <div className="text-xs font-bold text-muted mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {retroName}
                {group.date && (
                  <span style={{ fontWeight: 400, marginLeft: 6 }}>
                    {new Date(group.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </span>
                )}
              </div>
              <div className="flex-col gap-2">
                {group.items.map(item => (
                  <div
                    key={item.id}
                    style={{
                      background: 'var(--bg-card)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)',
                      padding: '10px 12px',
                      opacity: pendingId === item.id ? 0.5 : 1,
                      transition: 'opacity 0.15s'
                    }}
                  >
                    <div style={{ marginBottom: 8 }}>
                      <p className="text-sm" style={{ marginBottom: 4 }}>{item.content}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`badge ${item.type === 'action' ? 'badge-green' : 'badge-yellow'}`} style={{ fontSize: 9 }}>
                          {item.type === 'action' ? '⚡ Action' : '🧪 Change'}
                        </span>
                        {item.vote_count > 0 && (
                          <span className="badge badge-purple" style={{ fontSize: 9 }}>{item.vote_count} votes</span>
                        )}
                        {item.owner_name && (
                          <span className="badge badge-muted" style={{ fontSize: 9 }}>{item.owner_name}</span>
                        )}
                      </div>
                    </div>

                    {isFacilitator && (
                      <div className="flex gap-1 flex-wrap">
                        {Object.entries(OUTCOME).map(([key, cfg]) => (
                          <button
                            key={key}
                            className="btn btn-sm"
                            title={cfg.tip}
                            disabled={pendingId === item.id}
                            style={{
                              fontSize: 11,
                              padding: '2px 10px',
                              background: 'var(--bg-elevated)',
                              border: '1px solid var(--border)',
                              color: cfg.color
                            }}
                            onClick={() => handleOutcome(item, key)}
                          >
                            {cfg.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <p className="text-xs text-muted" style={{ marginTop: 4 }}>
            Items marked "✅ Worked" or "❌ Didn't work" are removed from this list. "🔄 Redo" carries the item into this sprint.
          </p>
        </div>
      )}
    </div>
  );
}
